import { GeneratedReportStore } from "@/stores/generated-report-store.core";
import {
   convertPeopleDataTableFilters,
   fieldTranslationKeyMap,
   legacyFieldNamesMap,
   sortFieldMaps,
} from "./helpers";
import { findCustomFieldByIntegrationName } from "@/react/shared/custom-field-utils";
import { FindPeopleSortBy } from "./people-list-enums";
import { ColumnEntityType } from "@laborchart-modules/common";
import type { CommonReportParams, ReportResponse } from "@/react/prop-types";
import { GenerateReportType, ReportType } from "@/react/prop-types";
import { Order } from "@laborchart-modules/common/dist/reql-builder/query-definitions";
import { mapServerFilters } from "@/react/shared/helper";
import type { ListServerFilter } from "../data-table/data-table-utils";

const ASSIGNMENT_HEADER_COLUMNS_WIDTH = 170;
const ASSIGNMENT_NESTED_COLUMNS_WIDTH = 90;
const ASSIGNMENT_JOB_NUMBER_COLUMN_WIDTH = 130;

const currentAssignmentsNestedFields = [
   "current_assignments_project_name",
   "current_assignments_job_number",
   "current_assignments_start_date",
   "current_assignments_end_date",
];
const nextAssignmentNestedFields = [
   "next_assignment_project_name",
   "next_assignment_job_number",
   "next_assignment_start_date",
   "next_assignment_end_date",
];

export async function createPeopleListReport(params: CommonReportParams): Promise<ReportResponse> {
   const { configKey, customFields, groupId, formValues, I18n, isLastNameFirst, search } = params;
   let customFieldId: string | null = null;
   let sortBy: FindPeopleSortBy = isLastNameFirst
      ? FindPeopleSortBy.PERSON_NAME_LAST
      : FindPeopleSortBy.PERSON_NAME_FIRST;
   const localStorageTableConfig = JSON.parse(localStorage.getItem(configKey) ?? "");
   const sortField = localStorageTableConfig.columnState.find((col: any) => col.sort !== null);
   if (sortField) {
      const customField = findCustomFieldByIntegrationName(customFields!, sortField.field);
      if (customField) {
         customFieldId = customField.id;
      }
      sortBy = customFieldId ? sortFieldMaps["custom_field"] : sortFieldMaps[sortField.field];
   }

   // Extract only the relevant sub-properties dynamically
   const getAssignmentSubProperties = (fields: string[], columns: any[]) =>
      fields
         .map((field) => {
            const matchedColumn = columns.find((col) => col.field === field);
            const fieldName = field.replace(/^(current_assignments_|next_assignment_)/, "");
            return {
               key: fieldName,
               name: I18n.t(fieldTranslationKeyMap[fieldName]),
               enabled: !!matchedColumn, // Enabled only if a matching column exists
               width: fieldName.includes("job_number")
                  ? ASSIGNMENT_JOB_NUMBER_COLUMN_WIDTH
                  : ASSIGNMENT_NESTED_COLUMNS_WIDTH,
            };
         })
         .filter(({ key }) => !["project_name"].includes(key)); // report api considers project name under current_assignments and next_assignment

   let hasCurrentAssignments = false;
   let hasNextAssignments = false;
   let sequenceIndex = 0;

   const filteredObjects = localStorageTableConfig.columnState.filter(
      (col: any) =>
         !col.hidden &&
         ![
            "type",
            "ag-Grid-AutoColumn",
            "current_assignments_project_name", // report api considers project_name under current_assignments its not a part of sub_properties
            "next_assignment_project_name", // report api considers project_name under next_assignment its not a part of sub_properties
         ].includes(col.field),
   );

   const mappedColumns = filteredObjects.reduce((acc: any[], col: any) => {
      if (currentAssignmentsNestedFields.includes(col.field)) {
         if (!hasCurrentAssignments) {
            acc.push({
               key: legacyFieldNamesMap["current_assignments"],
               name: I18n.t(fieldTranslationKeyMap["current_assignments"]),
               width: ASSIGNMENT_HEADER_COLUMNS_WIDTH,
               sequence: sequenceIndex++,
               base_name: null,
               sortable: true,
               sub_properties: getAssignmentSubProperties(
                  currentAssignmentsNestedFields,
                  filteredObjects,
               ),
            });
            hasCurrentAssignments = true;
         }
         return acc;
      }

      if (nextAssignmentNestedFields.includes(col.field)) {
         if (!hasNextAssignments) {
            acc.push({
               key: legacyFieldNamesMap["next_assignment"],
               name: I18n.t(fieldTranslationKeyMap["next_assignment"]),
               width: ASSIGNMENT_HEADER_COLUMNS_WIDTH,
               sequence: sequenceIndex++,
               base_name: null,
               sortable: true,
               sub_properties: getAssignmentSubProperties(
                  nextAssignmentNestedFields,
                  filteredObjects,
               ),
            });
            hasNextAssignments = true;
         }
         return acc;
      }

      const customField = findCustomFieldByIntegrationName(customFields!, col.field);
      let meta;
      const key = customField ? "custom_fields" : legacyFieldNamesMap[col.field];
      const name = customField ? customField.name : I18n.t(fieldTranslationKeyMap[col.field]);
      const base_name = customField ? customField.integration_name : null;

      // Add meta object when customField exists
      if (customField) {
         meta = {
            field_id: customField.id,
            field_property: customField.integration_name,
            field_type: customField.type,
            field_entity: ColumnEntityType.PEOPLE,
         };
      }

      acc.push({
         key,
         name,
         width: Math.round(col.width),
         sequence: sequenceIndex++,
         base_name,
         sortable: true,
         sub_properties: [],
         ...(customField && { meta }), // Add meta field only if customField exists
      });

      return acc;
   }, []);

   const updatedServerFilters = mapServerFilters(
      localStorageTableConfig.serverFilters,
      customFields!,
   );

   const payload: any = {
      sort_by: sortBy,
      sort_direction: sortField?.sort === "desc" ? Order.DESCENDING : Order.ASCENDING,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone, // This is how legacy report generation gets TZ
      report_type: ReportType.PeopleList,
      group_id: groupId,
      limit: 40,
      filters: convertPeopleDataTableFilters(updatedServerFilters as ListServerFilter[]),
      page_size: {
         height:
            formValues.orientation.id === "portrait"
               ? formValues.page_size.value.height
               : formValues.page_size.value.width,
         width:
            formValues.orientation.id === "portrait"
               ? formValues.page_size.value.width
               : formValues.page_size.value.height,
      },
      display_last_names_first: isLastNameFirst,
      file_type: formValues.file_type.value,
      column_headers: mappedColumns,
      ...(search && { search }), // add search if it exists
      ...(customFieldId && { custom_field_id: customFieldId }), // add custom_field_id if it exists
   };
   const report = await GeneratedReportStore.create(GenerateReportType.PeopleList, payload).payload;

   return report;
}
